/* React */
import React, { useCallback, useEffect } from "react";

/* React-Redux */
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/redux/store";
import { setMinMax } from "src/redux/features/insights-options/treatment-patterns/sankey-diagram/sankeyDiagramOptionsSlice";

import {
  clearOptions,
  setOptions,
} from "src/redux/features/insights-filter/insightsFilterOptionsSlice";

import { fetchCustomFeatures } from "src/redux/thunks/custom-cohorts/customFeatureThunk";

/* d3.js */
import * as d3 from "d3";

/* Thunks */
import { fetchTreatmentPatterns } from "src/redux/thunks/treatment-patterns/treatmentPatternsThunk";

/* Utils */
import useChartLoadingScreen from "src/utils/hooks/useChartLoadingScreen";
import { useFetchWithMainCondition } from "src/utils/hooks/useFetchWithMainCondition";
import { useIsCustomCohortActive } from "src/utils/hooks/useIsCustomCohortActive";
import useFilterCleanup from "src/utils/hooks/useFilterCleanup";
import ChartLayoutWrapper from "src/utils/wrapper/chart-layout-wrapper/ChartLayoutWrapper";

/* Layout */
import getUnstackedLayout from "src/utils/layout/unstacked-layout/getUnstackedLayout";

/* Constants */
import { CustomFeature } from "src/shared/constants/common/CustomFeature";
import { ChartType } from "src/shared/constants/charts/Chart";

/* Interfaces */
import { BaseParams } from "src/shared/interfaces/charts/FetchWithMainCondition";

const TreatmentPatterns: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  useFilterCleanup();

  const insightsFilterState = useSelector(
    (state: RootState) => state.insightsFilter
  );

  const { mainCondition, customFeature } =
    insightsFilterState;

  const treatmentPatternsState = useSelector(
    (state: RootState) => state.treatmentPatterns
  );

  const sankeyDiagramOptionsState = useSelector(
    (state: RootState) => state.sankeyDiagramOptions
  );

  const { maxDepth, width, showNoValueStates } =
    sankeyDiagramOptionsState;

  const { data, isLoading, error, modifiedAt } =
    treatmentPatternsState;

  const customCohortsState = useSelector(
    (state: RootState) => state.customCohorts
  );

  // No-value states have no well-defined denominator against a custom cohort's
  // value-conditioned sub-population, so the toggle is disabled and forced off
  // there - never send `include_no_value_states` for a custom cohort, even if
  // the slice still holds a stale "on" from a previous standard-cohort view.
  const isCustomCohortActive = useIsCustomCohortActive();

  useChartLoadingScreen(isLoading, modifiedAt);

  useEffect(() => {
    dispatch(fetchCustomFeatures());
  }, [dispatch]);

  useEffect(() => {
    if (!data?.data?.length) return;

    /**
     * Compute the min and max values for the links
     * in the Sankey diagram. Since there can be multiple diagrams,
     * we flatten the data to get all links and their values to compute
     * min and max globally for all diagrams.
     */
    const allValues = data.data
      .flatMap((item) => item.originalSankey?.links ?? [])
      .map((link) => link.value ?? 0);

    const min = Math.floor(d3.min(allValues) ?? 1);
    const max = Math.ceil(d3.max(allValues) ?? 100);

    dispatch(setMinMax({ min, max }));
  }, [data, dispatch]);

  const buildParams = useCallback(
    (base: BaseParams) => ({
      ...base,
      customFeature:
        customFeature?.value ?? CustomFeature.TREATMENT,
      maxDepth,
      width,
      includeNoValueStates:
        showNoValueStates && !isCustomCohortActive,
    }),
    [
      customFeature?.value,
      maxDepth,
      width,
      showNoValueStates,
      isCustomCohortActive,
    ]
  );

  useFetchWithMainCondition({
    mainCondition,
    debouncedValue: insightsFilterState,
    customCohortsState,
    fetchAction: fetchTreatmentPatterns,
    buildParams,
  });

  useEffect(() => {
    dispatch(setOptions(ChartType.SANKEY));
    return () => {
      dispatch(clearOptions());
    };
  }, [dispatch]);

  const tmpTypes = data?.types ?? [];
  const layout = getUnstackedLayout(tmpTypes, data!);

  return (
    <ChartLayoutWrapper layout={layout} error={error} />
  );
};

export default TreatmentPatterns;
